import { useEffect, useMemo, useState } from "react";
import { BrainCircuit, CalendarRange, Link2, Search } from "lucide-react";
import { AionSectionCard } from "@/components/aion/AionSectionCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { getBrainGraph, getBrainNode } from "@/services/brain";

const MEMORY_TYPES = ["", "PROJECT", "PERSON", "DECISION", "TASK", "COMMITMENT", "FACT", "INSIGHT", "PREFERENCE", "EVENT", "DEVICE", "LOCATION", "IDEA", "CONVERSATION"];

export const BrainView = ({ token, activeProfile }) => {
  const [query, setQuery] = useState("");
  const [memoryType, setMemoryType] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [graph, setGraph] = useState(null);
  const [selected, setSelected] = useState(null);
  const [status, setStatus] = useState("LOADING");
  const [error, setError] = useState("");

  const profileUserId = activeProfile?.user_id || "";

  const load = async () => {
    setStatus("LOADING");
    setError("");
    try {
      const data = await getBrainGraph({ query, memoryType, dateFrom, dateTo, profileUserId }, token);
      setGraph(data);
      setSelected(null);
      setStatus("READY");
    } catch (err) {
      setError(String(err?.message || "AION Brain konnte nicht geladen werden."));
      setStatus("ERROR");
    }
  };

  useEffect(() => {
    void load();
    // Profile changes must reload the permission-scoped graph.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileUserId, token]);

  const memoryNodes = useMemo(() => (graph?.nodes || []).filter((item) => item.kind === "memory"), [graph]);
  const topicNodes = useMemo(() => (graph?.nodes || []).filter((item) => item.kind === "topic"), [graph]);

  const openNode = async (nodeId) => {
    try {
      setSelected(await getBrainNode(nodeId, profileUserId, token));
    } catch (err) {
      setError(String(err?.message || "Brain-Detail konnte nicht geladen werden."));
    }
  };

  return (
    <div className="aion-view aion-view-stack" data-testid="brain-view">
      <section className="aion-briefing-hero" data-testid="brain-hero">
        <div className="aion-briefing-copy">
          <p className="aion-section-eyebrow">AION BRAIN · READ ONLY</p>
          <h1>Dein Wissensnetz</h1>
          <p>Erinnerungen, Personen, Projekte, Entscheidungen und Themen als verknüpfter Kontext – direkt aus dem bestehenden AION Memory.</p>
          <form className="aion-briefing-search" onSubmit={(event) => { event.preventDefault(); void load(); }}>
            <Search aria-hidden="true" />
            <Input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Brain durchsuchen …" data-testid="brain-search-input" />
            <Button type="submit" disabled={status === "LOADING"}>Prüfen</Button>
          </form>
        </div>
        <div className="aion-briefing-orb-wrap" aria-hidden="true">
          <div className="aion-briefing-orb"><BrainCircuit /></div>
          <div className="aion-briefing-orb-label">MEMORY GRAPH</div>
        </div>
      </section>

      <AionSectionCard title="Filter" eyebrow="KONTEXT" subtitle="Die Ansicht liest nur bereits vorhandene, für dieses Profil freigegebene Memory-Daten.">
        <div className="aion-compact-status-grid">
          <label className="aion-compact-status-box"><span>Typ</span><select value={memoryType} onChange={(event) => setMemoryType(event.target.value)} data-testid="brain-type-filter">{MEMORY_TYPES.map((item) => <option key={item || "all"} value={item}>{item || "Alle"}</option>)}</select></label>
          <label className="aion-compact-status-box"><span>Von</span><input type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} /></label>
          <label className="aion-compact-status-box"><span>Bis</span><input type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} /></label>
          <Button onClick={() => void load()} disabled={status === "LOADING"}><CalendarRange size={16} /> Anwenden</Button>
        </div>
      </AionSectionCard>

      {error ? <div className="aion-error-box" data-testid="brain-error">{error}</div> : null}

      <div className="aion-compact-status-grid" data-testid="brain-summary">
        <div className="aion-compact-status-box"><span>Knoten</span><strong>{graph?.summary?.nodes ?? "—"}</strong></div>
        <div className="aion-compact-status-box"><span>Verbindungen</span><strong>{graph?.summary?.edges ?? "—"}</strong></div>
        <div className="aion-compact-status-box"><span>Erinnerungen</span><strong>{graph?.summary?.memories ?? "—"}</strong></div>
        <div className="aion-compact-status-box"><span>Themen</span><strong>{graph?.summary?.topics ?? "—"}</strong></div>
        <div className="aion-compact-status-box"><span>Projekte</span><strong>{graph?.summary?.projects ?? "—"}</strong></div>
        <div className="aion-compact-status-box"><span>Modus</span><strong>{graph?.mode || "READ_ONLY"}</strong></div>
      </div>

      <AionSectionCard title="Wissensknoten" eyebrow="GRAPH" subtitle="Öffne einen Knoten, um seine direkten Beziehungen zu sehen." testId="brain-nodes-card">
        {status === "LOADING" ? <div className="aion-note">AION baut den Kontextgraphen …</div> : null}
        {status === "READY" && !memoryNodes.length ? <div className="aion-note">Für diesen Filter wurden keine Memory-Knoten gefunden.</div> : null}
        <div className="aion-list">
          {memoryNodes.slice(0, 40).map((node) => (
            <button className="aion-list-row" type="button" key={node.id} onClick={() => void openNode(node.id)} data-testid="brain-memory-node">
              <div className="aion-list-copy"><strong>{node.memory_type}</strong><small>{node.content}</small></div>
              <Link2 size={16} aria-hidden="true" />
            </button>
          ))}
        </div>
      </AionSectionCard>

      <AionSectionCard title="Themen" eyebrow="TOPICS" subtitle="Themen werden aus demselben Memory-Bestand abgeleitet.">
        <div className="aion-list">
          {topicNodes.slice(0, 30).map((node) => <div className="aion-list-row" key={node.id}><div className="aion-list-copy"><strong>{node.label}</strong><small>{node.topic_type || "Thema"}</small></div></div>)}
        </div>
      </AionSectionCard>

      {selected?.node ? (
        <AionSectionCard title={selected.node.label} eyebrow="DETAIL" subtitle="Direkte Verbindungen dieses Brain-Knotens." testId="brain-detail-card">
          <p>{selected.node.content || selected.node.label}</p>
          <div className="aion-list">
            {(selected.neighbors || []).map((neighbor) => <div className="aion-list-row" key={neighbor.id}><div className="aion-list-copy"><strong>{neighbor.kind}</strong><small>{neighbor.label}</small></div></div>)}
          </div>
        </AionSectionCard>
      ) : null}
    </div>
  );
};
